import { useMemo, useState } from "react";
import { taskService } from "@miplatform/api";
import {
  ATTEMPT_STATUSES,
  ATTEMPT_STATUS_COLORS,
  EMAIL_TYPE,
  HTTP_STATUSES,
  PERMISSIONS,
  SUBMIT_STATUSES,
  SUBMIT_STATUS_COLORS,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { MiTHEME } from "@miplatform/theme";
import { isEmpty, isSameOrAfter, isToday } from "@miplatform/utils";
import {
  mdiCalendarRemove,
  mdiCancel,
  mdiCheck,
  mdiClose,
  mdiEmailOutline,
  mdiFormatListBulletedType,
  mdiHandFrontLeft,
  mdiListStatus,
  mdiLockOpenOutline,
  mdiLockOutline,
  mdiPlay,
  mdiRayStartArrow,
} from "@mdi/js";
import { useGlobalManagerApproval } from "../../../../../components/GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";
import { usePermissions } from "../../../../../hooks";

const MENU_LABELS = {
  ADD_TECHNICIAN: "Add Technician",
  HOLD: "Hold Order",
  LOCK: "Lock",
  DISPATCH: "Dispatch",
  UNSCHEDULE: "Unschedule",
  EMAIL_APPOINTMENT_CONFIRMATION: "Email Appt Confirmation",
  EMAIL_TECHNICIAN_ENROUTE: "Email Tech Enroute",
  EMAIL_ORDER_ASSIGNMENT: "Email O Assignment",
  EMAIL_CLOSEOUT_AND_UPDATE: "Email Closeout & O Update",
  UNLOCK: "Unlock",
  REMOVE: "Remove",
  ACCEPT: "Accept",
  DETAILS: "Details",
  START: "Start",
  SUBMIT: "Submit",
  JOB_COMPLETE: "Job Complete",
  JOB_INCOMPLETE: "Job Incomplete",
  CLOSEOUT: "Closeout",
  FORCECLOSEOUT: "Force Closeout",
  CANCEL: "Cancel",
};

export const useTaskCardMenuViewModel = ({
  attempt,
  isInBucket,
  closeContextMenu,
  modalType,
  setModalType,
  isCalendar,
  setHasModalOpen,
  handleOpenCloseoutDrawer = () => null,
  onEmailDrawerOpen = () => null,
  onAttemptUpdated = () => null,
}) => {
  const { executeApiRequest } = useGlobalManagerApproval();

  const { hasPermission } = usePermissions();

  const stateManager = useComponentState();

  const [defaultModalType, setDefaultModalType] = useState("");
  const isIssuesResolved = useMemo(
    () => attempt.issues?.every((issue) => issue.issueResult),
    [attempt.issues]
  );

  const openModal = (option) => () => {
    closeContextMenu();
    setModalType(option);
    setDefaultModalType(option);
    setHasModalOpen(true);
  };

  const closeoutAttempt = () => {
    openAttemptDetails();

    stateManager.run(async () => {
      const response = await executeApiRequest({
        options: await taskService.attempts.getCloseoutInProgressOptions({
          data: { attemptId: attempt.id },
          modifiedOn: attempt.modifiedOn,
        }),
        toastMessageForManagerApproval: {
          text: "Manager Approval Required",
          subtext:
            "The force closeout of a passed attempt requires manager approval",
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        onAttemptUpdated(response.data);
      }
    });
  };

  const unlockAttempt = () => {
    stateManager.run(async () => {
      const options = await taskService.attempts.getUnlockOptions({
        attemptId: attempt.id,
        modifiedOn: attempt.modifiedOn,
      });

      await executeApiRequest({
        options,
        toastMessageForManagerApproval: {
          text: "Manager Approval Required",
          subtext:
            "The user is trying to unlock an order which has been locked to: Date, time and user.",
        },
        successToastMessageForManagerApproval: {
          text: `${attempt.assignment.order.orderTemplateName} at ${attempt.home.address.addressLine1} has been unlocked.`,
        },
        callback: onAttemptUpdated,
      });
    });
  };

  const openAttemptDetails = () => {
    closeContextMenu();
    handleOpenCloseoutDrawer(attempt);
  };

  const submitMenuOptions = [
    {
      label: MENU_LABELS.JOB_COMPLETE,
      icon: mdiCheck,
      color: SUBMIT_STATUS_COLORS[SUBMIT_STATUSES.COMPLETE],
      action: openModal(MENU_LABELS.JOB_COMPLETE),
    },
    {
      label: MENU_LABELS.JOB_INCOMPLETE,
      icon: mdiClose,
      color: SUBMIT_STATUS_COLORS[SUBMIT_STATUSES.INCOMPLETE],
      action: openModal(MENU_LABELS.JOB_INCOMPLETE),
    },
  ];

  const getMenuOptions = () => {
    const menuOptions = [
      {
        label: MENU_LABELS.DETAILS,
        icon: mdiFormatListBulletedType,
        color: MiTHEME.palette.blue.main,
        action: openAttemptDetails,
      },
    ];

    if (
      attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED ||
      (attempt.attemptStatus === ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS &&
        isIssuesResolved)
    ) {
      menuOptions.push({
        label: MENU_LABELS.EMAIL_CLOSEOUT_AND_UPDATE,
        icon: mdiEmailOutline,
        color: MiTHEME.palette.green.main,
        action: () =>
          onEmailDrawerOpen({ attempt, type: EMAIL_TYPE.CLOSEOUT_AND_UPDATE }),
      });
    }

    if (attempt.displayStartTime) {
      if (isToday(attempt.displayStartTime)) {
        if (attempt.attemptStatus === ATTEMPT_STATUSES.PLANNED) {
          menuOptions.push({
            label: MENU_LABELS.DISPATCH,
            icon: mdiRayStartArrow,
            color: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.DISPATCHED],
            action: openModal(MENU_LABELS.DISPATCH),
          });
        }

        if (attempt.attemptStatus === ATTEMPT_STATUSES.DISPATCHED) {
          menuOptions.push({
            label: MENU_LABELS.EMAIL_TECHNICIAN_ENROUTE,
            icon: mdiEmailOutline,
            color: MiTHEME.palette.primary.main,
            action: () =>
              onEmailDrawerOpen({
                attempt,
                type: EMAIL_TYPE.TECHNICIAN_ENROUTE,
              }),
          });

          menuOptions.push({
            label: MENU_LABELS.EMAIL_ORDER_ASSIGNMENT,
            icon: mdiEmailOutline,
            color: MiTHEME.palette.cyan.main,
            action: () =>
              onEmailDrawerOpen({ attempt, type: EMAIL_TYPE.ORDER_ASSIGNMENT }),
          });
        }

        if (
          [ATTEMPT_STATUSES.PLANNED, ATTEMPT_STATUSES.DISPATCHED].includes(
            attempt.attemptStatus
          )
        ) {
          menuOptions.push({
            label: MENU_LABELS.START,
            icon: mdiPlay,
            color: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.STARTED],
            action: openModal(MENU_LABELS.START),
            disabled: !attempt.ableToStart,
          });
        }

        if (attempt.attemptStatus === ATTEMPT_STATUSES.STARTED) {
          menuOptions.push({
            label: MENU_LABELS.SUBMIT,
            icon: mdiListStatus,
            color: MiTHEME.palette.grey.main,
            items: submitMenuOptions,
          });
        }
      }

      if (
        [
          ATTEMPT_STATUSES.SUBMITTED,
          ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS,
        ].includes(attempt.attemptStatus)
      ) {
        menuOptions.push({
          label: MENU_LABELS.CLOSEOUT,
          icon: mdiListStatus,
          color: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS],
          action: closeoutAttempt,
        });
      }

      if (
        ![
          ATTEMPT_STATUSES.SUBMITTED,
          ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS,
          ATTEMPT_STATUSES.CLOSED,
        ].includes(attempt.attemptStatus) &&
        attempt.scheduledStartDate !== null &&
        !isSameOrAfter(attempt.scheduledStartDate)
      ) {
        menuOptions.push({
          label: MENU_LABELS.FORCECLOSEOUT,
          icon: mdiListStatus,
          color: ATTEMPT_STATUS_COLORS[ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS],
          action: closeoutAttempt,
        });
      }
    }

    if (attempt.noDrag || attempt.hasProposed) {
      return menuOptions;
    }

    if (isInBucket) {
      menuOptions.push({
        label: MENU_LABELS.HOLD,
        icon: mdiHandFrontLeft,
        color: MiTHEME.palette.purple.main,
        action: openModal(MENU_LABELS.HOLD),
      });

      menuOptions.push({
        label: MENU_LABELS.CANCEL,
        icon: mdiCancel,
        color: MiTHEME.palette.red.dark,
        action: openModal(MENU_LABELS.CANCEL),
      });

      return menuOptions;
    }

    menuOptions.push({
      label: MENU_LABELS.UNSCHEDULE,
      icon: mdiCalendarRemove,
      color: MiTHEME.palette.red.main,
      action: openModal(MENU_LABELS.UNSCHEDULE),
    });

    if (attempt.attemptStatus === ATTEMPT_STATUSES.PLANNED) {
      menuOptions.push({
        label: MENU_LABELS.EMAIL_APPOINTMENT_CONFIRMATION,
        icon: mdiEmailOutline,
        color: MiTHEME.palette.primary.main,
        action: () => onEmailDrawerOpen({ attempt, type: EMAIL_TYPE.SCHEDULE }),
      });
      menuOptions.push({
        label: MENU_LABELS.EMAIL_ORDER_ASSIGNMENT,
        icon: mdiEmailOutline,
        color: MiTHEME.palette.cyan.main,
        action: () =>
          onEmailDrawerOpen({ attempt, type: EMAIL_TYPE.ORDER_ASSIGNMENT }),
      });
    }

    menuOptions.push({
      label: MENU_LABELS.HOLD,
      icon: mdiHandFrontLeft,
      color: MiTHEME.palette.purple.main,
      action: openModal(MENU_LABELS.HOLD),
    });

    if (
      isEmpty(attempt.lockTypes) &&
      hasPermission(PERMISSIONS.APPOINTMENT.LOCK)
    ) {
      menuOptions.push({
        label: MENU_LABELS.LOCK,
        icon: mdiLockOutline,
        color: MiTHEME.palette.common.black,
        action: openModal(MENU_LABELS.LOCK),
      });
    }

    if (!isEmpty(attempt.lockTypes)) {
      menuOptions.push({
        label: MENU_LABELS.UNLOCK,
        icon: mdiLockOpenOutline,
        color: MiTHEME.palette.common.black,
        action: unlockAttempt,
      });
    }

    menuOptions.push({
      label: MENU_LABELS.CANCEL,
      icon: mdiCancel,
      color: MiTHEME.palette.red.dark,
      action: openModal(MENU_LABELS.CANCEL),
    });

    return menuOptions;
  };

  const closeModal = () => {
    setModalType("");
    setDefaultModalType("");
    setHasModalOpen(false);
  };

  return {
    menuOptions: getMenuOptions(),
    MENU_LABELS,
    closeModal,
    modalTypeState: isCalendar ? modalType : defaultModalType,
    setModalTypeState: isCalendar ? setModalType : setDefaultModalType,
  };
};
